import Vue from 'vue'
import { S, G, M, A } from './types'
import { Getters, Mutations, Actions } from 'vuex'
import dayjs from 'dayjs'
import { extractCursor } from '@/api/util'

/* -------------------------------------------------------------------------- */

const state = (): S => ({
  nextCursor: '',
  prevCursor: '',
  currentCursor: '',
  hd: null,
  bpList: [],
  msList: []
})

/* -------------------------------------------------------------------------- */

const getters: Getters<S, G> = {
  hd: state => state.hd,
  setting: state => (state.hd ? state.hd.setting : null),
  bpList: state =>
    state.bpList.slice().sort((a, b) => {
      const aTime = dayjs(a.measureDatetime)
        .toDate()
        .getTime()
      const bTime = dayjs(b.measureDatetime)
        .toDate()
        .getTime()
      return aTime - bTime
    }),
  msList: state =>
    state.msList.slice().sort((a, b) => {
      const aTime = dayjs(a.dialysisDatetime)
        .toDate()
        .getTime()
      const bTime = dayjs(b.dialysisDatetime)
        .toDate()
        .getTime()
      return aTime - bTime
    }),
  latestBp: state => {
    const sorted = state.bpList.slice().sort((a, b) => {
      const aTime = dayjs(a.measureDatetime)
        .toDate()
        .getTime()
      const bTime = dayjs(b.measureDatetime)
        .toDate()
        .getTime()
      return bTime - aTime
    })
    return sorted.length ? sorted[0] : null
  },
  latestMs: state => {
    const sorted = state.msList.slice().sort((a, b) => {
      const aTime = dayjs(a.dialysisDatetime)
        .toDate()
        .getTime()
      const bTime = dayjs(b.dialysisDatetime)
        .toDate()
        .getTime()
      return bTime - aTime
    })
    return sorted.length ? sorted[0] : null
  },
  hasNext: state => !!state.nextCursor,
  hasPrev: state => !!state.prevCursor,
  hdType: state => (state.hd ? state.hd.setting.dialysisType : '血液浄化法')
}

/* -------------------------------------------------------------------------- */

const mutations: Mutations<S, M> = {
  updateHd: (state, hd) => {
    state.hd = hd
    if (!hd) return
    state.bpList = hd.bpList
    state.msList = hd.msList
  },
  updateNextCursor: (state, url) => {
    state.nextCursor = extractCursor(url)
  },
  updatePrevCursor: (state, url) => {
    state.prevCursor = extractCursor(url)
  },
  updateCurrentCursor: (state, cursor) => {
    state.currentCursor = cursor
  }
}

/* -------------------------------------------------------------------------- */

const actions: Actions<S, A, G, M> = {
  fetchLatestHd: async ({ commit }, { patientId, currentTab }) => {
    const res = await Vue.$api.hd.fetch({ patientId, currentTab })
    commit('updateNextCursor', res.next || '')
    commit('updatePrevCursor', res.previous || '')
    commit('updateHd', res.results[0] || null)
  },
  fetchNextHd: async ({ commit, state }, { patientId }) => {
    const cursor = state.nextCursor
    if (!cursor) return
    const res = await Vue.$api.hd.fetch({ patientId, cursor })
    commit('updateCurrentCursor', cursor)
    commit('updateNextCursor', res.next || '')
    commit('updatePrevCursor', res.previous || '')
    commit('updateHd', res.results[0] || null)
  },
  fetchPrevHd: async ({ commit, state }, { patientId }) => {
    const cursor = state.prevCursor
    if (!cursor) return
    const res = await Vue.$api.hd.fetch({ patientId, cursor })
    commit('updateCurrentCursor', cursor)
    commit('updateNextCursor', res.next || '')
    commit('updatePrevCursor', res.previous || '')
    commit('updateHd', res.results[0] || null)
  },
  refreshCurrentHd: async ({ commit, state }, { patientId }) => {
    const cursor = state.currentCursor
    const res = await Vue.$api.hd.fetch({ patientId, cursor })
    commit('updateCurrentCursor', cursor)
    commit('updateNextCursor', res.next || '')
    commit('updatePrevCursor', res.previous || '')
    commit('updateHd', res.results[0] || null)
  },
  initHd: async ({ commit }) => {
    commit('updateCurrentCursor', '')
    commit('updateNextCursor', '')
    commit('updatePrevCursor', '')
  }
}

/* -------------------------------------------------------------------------- */

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
